/**
 * Client-side backlog health scoring for board columns.
 * Pure helpers — no Redux / network.
 */

export type BoardHealthDetail = 'ok' | 'thin' | 'empty';
export type BoardHealthAction = 'keep' | 'enrich' | 'delete';
export type BoardHealthAgeBucket = 'lt90' | 'd90_365' | 'y1_2' | 'gt2y' | 'unknown';
export type BoardHealthEffort = 'unset' | 'easy' | 'medium' | 'heavy';
export type BoardHealthValue = 'low' | 'medium' | 'high';
export type BoardHealthFocus = 'doNow' | 'quick' | 'urgent' | 'zombie';

export type BoardHealthChangeLike = {
    _id: string;
    slug?: number;
    type?: string;
    title?: Record<string, string> | string | null;
    description?: Record<string, string> | string | null;
    developer_notes?: string | null;
    technicalContext?: { legacyNotes?: string | null } | null;
    estimate?: number | null;
    importance?: number | null;
    priority?: number | null;
    createdAt?: string | null;
    progress?: { status?: string | null } | null;
};

export type BoardHealthScoredChange = {
    change: BoardHealthChangeLike;
    title: string;
    slugLabel: string;
    detail: BoardHealthDetail;
    action: BoardHealthAction;
    effort: BoardHealthEffort;
    value: BoardHealthValue;
    urgency: number;
    ageDays: number | null;
    ageBucket: BoardHealthAgeBucket;
    descriptionLength: number;
    notesLength: number;
};

export type BoardHealthReport = {
    total: number;
    byDetail: Record<BoardHealthDetail, number>;
    byAction: Record<BoardHealthAction, number>;
    byAge: Record<BoardHealthAgeBucket, number>;
    estimateMissing: number;
    estimateHours: number;
    items: BoardHealthScoredChange[];
    doNow: BoardHealthScoredChange[];
    quickWins: BoardHealthScoredChange[];
    urgent: BoardHealthScoredChange[];
    zombies: BoardHealthScoredChange[];
};

/** Busy / late-pipeline — exclude from "do now" / quick wins. */
const BUSY_STATUSES = new Set([
    'inProgress',
    'teamReview',
    'clientReview',
]);

const PROTECTED_STATUSES = new Set([
    'inProgress',
    'teamReview',
    'clientReview',
    'pendingDeployment',
    'deployed',
    'finished',
    'merged',
]);

const DAY_MS = 24 * 60 * 60 * 1000;

export function plainText(value: string | null | undefined): string {
    let text = String(value || '');
    if (!text)
        return '';

    text = text
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return text;
}

export function localizedField(
    value: Record<string, string> | string | null | undefined,
    lang: string,
): string {
    if (!value)
        return '';
    if (typeof value === 'string')
        return value;
    if (lang in value && value[lang])
        return value[lang];
    const keys = Object.keys(value);
    return keys.length ? (value[keys[0]] || '') : '';
}

export function notesText(change: BoardHealthChangeLike): string {
    const notes = change.developer_notes || change.technicalContext?.legacyNotes || '';
    return plainText(notes);
}

export function slugLabel(change: BoardHealthChangeLike): string {
    const slug = change.slug ?? 0;
    const type = change.type || '';
    if (type === 'feature')
        return `F${slug}`;
    if (type === 'fix')
        return `B${slug}`;
    if (!type || type === 'none')
        return `S${slug}`;
    return `C${slug}`;
}

export function ageDays(change: BoardHealthChangeLike, nowMs = Date.now()): number | null {
    if (!change.createdAt)
        return null;
    const created = Date.parse(change.createdAt);
    if (Number.isNaN(created))
        return null;
    return Math.max(0, Math.floor((nowMs - created) / DAY_MS));
}

export function ageBucket(days: number | null): BoardHealthAgeBucket {
    if (days == null)
        return 'unknown';
    if (days < 90)
        return 'lt90';
    if (days < 365)
        return 'd90_365';
    if (days < 730)
        return 'y1_2';
    return 'gt2y';
}

/** Estimate hours → effort bucket (canvas: ≤1j / 1–3j / ≥5j). */
export function scoreEffort(estimate: number | null | undefined): BoardHealthEffort {
    if (estimate == null || estimate <= 0)
        return 'unset';
    if (estimate <= 1)
        return 'easy';
    if (estimate <= 3)
        return 'medium';
    return 'heavy';
}

/** Priority / importance → business value (canvas haute / moyenne / basse). */
export function scoreValue(change: BoardHealthChangeLike): BoardHealthValue {
    const priority = change.priority ?? 0;
    const importance = change.importance ?? 0;
    if (importance >= 3 || priority >= 15)
        return 'high';
    if (importance >= 2 || priority >= 5)
        return 'medium';
    return 'low';
}

/** Sort key aligned with canvas urgency (priority + importance weight + busy boost). */
export function scoreUrgency(change: BoardHealthChangeLike): number {
    const priority = change.priority ?? 0;
    const importance = change.importance ?? 0;
    const status = change.progress?.status || '';
    const busyBoost = BUSY_STATUSES.has(status) ? 7 : 0;
    return priority + importance * 5 + busyBoost;
}

function isBusy(change: BoardHealthChangeLike): boolean {
    return BUSY_STATUSES.has(change.progress?.status || '');
}

function byUrgencyDesc(a: BoardHealthScoredChange, b: BoardHealthScoredChange): number {
    return b.urgency - a.urgency || (b.change.priority ?? 0) - (a.change.priority ?? 0);
}

export function itemsDoNow(items: BoardHealthScoredChange[]): BoardHealthScoredChange[] {
    return items
        .filter((item) =>
            item.value === 'high' &&
            (item.effort === 'easy' || item.effort === 'medium') &&
            !isBusy(item.change) &&
            item.action !== 'delete'
        )
        .sort(byUrgencyDesc);
}

export function itemsQuickWins(items: BoardHealthScoredChange[]): BoardHealthScoredChange[] {
    return items
        .filter((item) => {
            const estimate = item.change.estimate ?? 0;
            return (
                estimate > 0 &&
                estimate <= 1 &&
                !isBusy(item.change) &&
                item.action !== 'delete'
            );
        })
        .sort((a, b) => byUrgencyDesc(a, b) || (a.change.estimate ?? 0) - (b.change.estimate ?? 0));
}

export function itemsUrgent(items: BoardHealthScoredChange[]): BoardHealthScoredChange[] {
    return items
        .filter((item) => (item.change.priority ?? 0) >= 15 || (item.change.importance ?? 0) >= 3)
        .sort(byUrgencyDesc);
}

export function itemsZombies(items: BoardHealthScoredChange[]): BoardHealthScoredChange[] {
    return items
        .filter((item) => (item.ageDays ?? 0) >= 730)
        .sort((a, b) => (b.ageDays ?? 0) - (a.ageDays ?? 0));
}

export function scoreDetail(change: BoardHealthChangeLike, lang: string): BoardHealthDetail {
    const title = plainText(localizedField(change.title, lang));
    const description = plainText(localizedField(change.description, lang));
    const notes = notesText(change);

    const titleLen = title.length;
    const descLen = description.length;
    const notesLen = notes.length;

    if (notesLen >= 40 || descLen >= 80)
        return 'ok';

    if (descLen >= 40 || notesLen >= 20 || titleLen >= 50)
        return 'thin';

    const titleVague =
        titleLen < 20 ||
        /\?\s*$/.test(title) ||
        /^(todo|wip|test|fix|bug|idée|idea)\b/i.test(title);

    if (titleVague || (descLen < 20 && notesLen < 10 && titleLen < 35))
        return 'empty';

    return 'thin';
}

export function scoreAction(
    change: BoardHealthChangeLike,
    detail: BoardHealthDetail,
    lang = 'fr',
): BoardHealthAction {
    const status = change.progress?.status || '';
    if (PROTECTED_STATUSES.has(status))
        return 'keep';

    const priority = change.priority ?? 0;
    const importance = change.importance ?? 0;
    const descLen = plainText(localizedField(change.description, lang)).length;
    const notesLen = notesText(change).length;

    if (detail === 'empty') {
        if (priority >= 10 || importance >= 4)
            return 'enrich';
        return 'delete';
    }

    if (
        detail === 'thin' &&
        (priority >= 15 || importance >= 4 || (descLen < 20 && notesLen < 20))
    ) {
        return 'enrich';
    }

    return 'keep';
}

export function scoreChange(
    change: BoardHealthChangeLike,
    lang: string,
    nowMs = Date.now(),
): BoardHealthScoredChange {
    const detail = scoreDetail(change, lang);
    const days = ageDays(change, nowMs);

    return {
        change,
        title: plainText(localizedField(change.title, lang)) || slugLabel(change),
        slugLabel: slugLabel(change),
        detail,
        action: scoreAction(change, detail, lang),
        effort: scoreEffort(change.estimate),
        value: scoreValue(change),
        urgency: scoreUrgency(change),
        ageDays: days,
        ageBucket: ageBucket(days),
        descriptionLength: plainText(localizedField(change.description, lang)).length,
        notesLength: notesText(change).length,
    };
}

export function analyzeChanges(
    changes: BoardHealthChangeLike[],
    lang: string,
    nowMs = Date.now(),
): BoardHealthReport {
    const items = (changes || []).map((change) => scoreChange(change, lang, nowMs));

    const byDetail: Record<BoardHealthDetail, number> = { ok: 0, thin: 0, empty: 0 };
    const byAction: Record<BoardHealthAction, number> = { keep: 0, enrich: 0, delete: 0 };
    const byAge: Record<BoardHealthAgeBucket, number> = {
        lt90: 0,
        d90_365: 0,
        y1_2: 0,
        gt2y: 0,
        unknown: 0,
    };

    let estimateMissing = 0;
    let estimateHours = 0;

    items.forEach((item) => {
        byDetail[item.detail] += 1;
        byAction[item.action] += 1;
        byAge[item.ageBucket] += 1;
        const estimate = item.change.estimate;
        if (estimate == null || estimate <= 0)
            estimateMissing += 1;
        else
            estimateHours += estimate;
    });

    return {
        total: items.length,
        byDetail,
        byAction,
        byAge,
        estimateMissing,
        estimateHours,
        items,
        doNow: itemsDoNow(items),
        quickWins: itemsQuickWins(items),
        urgent: itemsUrgent(items),
        zombies: itemsZombies(items),
    };
}

/** Empty select value = whole board (no column filter). */
export const BOARD_HEALTH_ALL_COLUMNS = '';

export function pickDefaultColumnId(
    columns: Array<{ _id: string; title?: string; changes?: unknown[] }>,
): string | undefined {
    if (!columns?.length)
        return undefined;

    const backlog = columns.find((column) => /backlog/i.test(column.title || ''));
    if (backlog)
        return backlog._id;

    const withCards = columns.find((column) => (column.changes || []).length > 0);
    return (withCards || columns[0])._id;
}

/** Flatten board columns and dedupe by change `_id` (smart columns may overlap). */
export function collectBoardChanges(
    columns: Array<{ changes?: BoardHealthChangeLike[] | null }>,
): BoardHealthChangeLike[] {
    const seen = new Set<string>();
    const out: BoardHealthChangeLike[] = [];

    (columns || []).forEach((column) => {
        (column.changes || []).forEach((change) => {
            if (!change || !change._id || seen.has(change._id))
                return;
            seen.add(change._id);
            out.push(change);
        });
    });

    return out;
}

export function itemsForAction(
    report: BoardHealthReport,
    action: BoardHealthAction,
): BoardHealthScoredChange[] {
    return report.items
        .filter((item) => item.action === action)
        .sort((a, b) => (b.change.priority ?? 0) - (a.change.priority ?? 0));
}
